import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Play, FileSpreadsheet, FileText, FileDown, ShoppingCart, Receipt, TrendingUp, Landmark, Share2 } from "lucide-react";
import { ShareDialog, SharedReports } from "@/components/VatShare";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, EmptyState, Spinner, StatCard, Money } from "@/components/common";
import SearchSelect from "@/components/SearchSelect";
import PeriodBar, { rangeFor, returnRange, returnLabel } from "@/components/PeriodBar";
import { api, downloadFile, formatErr } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const PERIODS = ["prev_return", "cur_return", "today", "month", "last_month", "last3", "last6", "fy", "last_fy", "custom"];

const NetBanner = ({ s }) => {
  const payable = s.position === "payable";
  const tone = payable ? "bg-red-50 border-red-200 text-red-900" : s.position === "refundable" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-slate-50 border-slate-200 text-slate-800";
  const label = payable ? "Net VAT payable to government (ZATCA)" : s.position === "refundable" ? "Net VAT paid in excess — refundable / carry-forward credit" : "Net VAT position: nil";
  return (
    <div className={`panel border-2 ${tone} p-6 flex flex-wrap items-center gap-6 fade-up`} data-testid="vat-net-banner">
      <Landmark className="w-8 h-8 shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold opacity-80">{label}</div>
        <div className="num text-3xl font-semibold mt-1" data-testid="vat-net-amount">{fmtMoney(Math.abs(s.net_vat))} <span className="text-sm opacity-70">SAR</span></div>
      </div>
      <div className="num text-xs opacity-80">Sales VAT {fmtMoney(s.sales_vat)} − Purchase VAT {fmtMoney(s.purchase_vat)} = {fmtMoney(s.net_vat)}</div>
    </div>
  );
};

const Table = ({ title, head, rows, testid }) => (
  <div className="panel overflow-hidden fade-up" data-testid={testid}>
    <div className="px-4 py-3 border-b border-slate-200 font-display font-semibold">{title} <span className="num text-xs text-slate-400 ml-1">{rows.length}</span></div>
    <div className="overflow-x-auto max-h-[420px]">
      {!rows.length ? <EmptyState title="No entries in this period" /> : (
        <table className="w-full"><thead className="sticky top-0"><tr>{head.map(([h, r]) => <th key={h} className={`th ${r ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
          <tbody>{rows.map((cells, i) => <tr key={i} className="row-hover">{cells.map((c, j) => <td key={j} className={`td text-sm ${head[j][1] ? "text-right" : ""}`}>{c}</td>)}</tr>)}</tbody>
        </table>
      )}
    </div>
  </div>
);

export default function VatConsolidation() {
  const { user, settings } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [period, setPeriod] = useState("prev_return");
  const [range, setRange] = useState(returnRange("monthly", -1));
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState(false);
  const [sharedTick, setSharedTick] = useState(0);
  const canShare = ["super_admin", "staff"].includes(user.role);
  const canExport = settings?.export_roles?.includes(user.role);

  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)); }, []);
  const params = { client_id: clientId, date_from: range[0], date_to: range[1] };

  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get("/vat-consolidation", { params }); setRes(data); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const exp = async (fmt) => {
    try { await downloadFile("/vat-consolidation", { ...params, format: fmt }, `vat-consolidation.${fmt}`); toast.success(`${fmt.toUpperCase()} downloaded`); } catch (e) { toast.error("Export failed"); }
  };
  const client = clients.find((c) => c.id === clientId);
  const freq = client?.vat_filing_frequency || "monthly";
  const resolve = (p, fq = freq) => (p === "prev_return" ? returnRange(fq, -1) : p === "cur_return" ? returnRange(fq, 0) : rangeFor(p));
  const pick = (p) => { setPeriod(p); if (p !== "custom") setRange(resolve(p)); setRes(null); };
  const pickClient = (v) => {
    setClientId(v);
    setRes(null);
    const fq = clients.find((c) => c.id === v)?.vat_filing_frequency || "monthly";
    setPeriod("prev_return");
    setRange(resolve("prev_return", fq));
  };
  const s = res?.summary;
  const [qs, setQs] = useSearchParams();
  const qClient = qs.get("client");
  useEffect(() => {
    if (qClient && clients.some((c) => c.id === qClient)) { pickClient(qClient); setQs({}, { replace: true }); }
  }, [qClient, clients]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="VAT" title="VAT payable consolidation" subtitle="Output VAT on sales minus input VAT on purchases for a client and period. Purchases by invoice date; sales entries whose period falls within the range." />
      <div className="panel p-4 space-y-4 fade-up">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-[380px]">
            <div className="text-[11px] font-medium text-slate-600 mb-1 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-emerald-700" />Client company <span className="text-red-500">*</span></div>
            <SearchSelect value={clientId} onChange={pickClient} options={clients.map((c) => [c.id, `${c.company_name} · ${c.code}`])} placeholder="Type to search client…" testid="vat-filter-client" />
          </div>
          {client && (
            <div className="pb-1.5 flex items-center gap-2 text-xs text-slate-600" data-testid="vat-filing-info">
              <span className="font-medium px-2 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-700">{freq === "quarterly" ? "Quarterly filer" : "Monthly filer"}</span>
              {period.endsWith("_return") && <span>Return period: <span className="font-semibold text-slate-900" data-testid="vat-return-label">{returnLabel(freq, range[0])}</span></span>}
            </div>
          )}
        </div>
        <PeriodBar periods={PERIODS} period={period} from={range[0]} to={range[1]} onPeriod={pick} onFrom={(v) => { setRange([v, range[1]]); setRes(null); }} onTo={(v) => { setRange([range[0], v]); setRes(null); }} />
        <div className="flex flex-wrap gap-2">
          <Button onClick={run} disabled={!clientId || busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="vat-run-button"><Play className="w-4 h-4 mr-1.5" />{busy ? "Calculating…" : "Calculate VAT position"}</Button>
          {canExport && res && <>
            <Button variant="outline" onClick={() => exp("pdf")} data-testid="vat-export-pdf"><FileText className="w-4 h-4 mr-1.5 text-red-600" />PDF</Button>
            <Button variant="outline" onClick={() => exp("xlsx")} data-testid="vat-export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1.5 text-emerald-700" />Excel</Button>
            <Button variant="outline" onClick={() => exp("csv")} data-testid="vat-export-csv"><FileDown className="w-4 h-4 mr-1.5" />CSV</Button>
          </>}
          {canShare && res && <Button onClick={() => setShare(true)} className="bg-amber-500 hover:bg-amber-600 text-[#064E3B] font-semibold" data-testid="vat-share-button"><Share2 className="w-4 h-4 mr-1.5" />Share with client</Button>}
        </div>
      </div>
      {busy && !res ? <Spinner /> : !res ? <div className="panel"><EmptyState title="Select a client and period, then calculate" testid="vat-empty" /></div> : (<>
        <div className="text-sm text-slate-600"><span className="font-semibold text-slate-900">{res.client.company_name}</span> <span className="num text-xs">· {res.client.code} · VAT {res.client.vat_number} · {fmtDate(res.date_from)} – {fmtDate(res.date_to)}</span></div>
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4" data-testid="vat-summary-cards">
          <StatCard label="Total purchases (incl. VAT)" value={fmtMoney(s.purchase_total)} hint={`SAR · ${s.purchase_count} bills`} icon={ShoppingCart} tone="slate" testid="vat-purchase-total" />
          <StatCard label="Purchase VAT (input)" value={fmtMoney(s.purchase_vat)} hint="SAR" icon={Receipt} tone="sky" testid="vat-purchase-vat" delay={50} />
          <StatCard label="Total sales (incl. VAT)" value={fmtMoney(s.sales_total)} hint={`SAR · ${s.sales_count} entries`} icon={TrendingUp} testid="vat-sales-total" delay={100} />
          <StatCard label="Sales VAT (output)" value={fmtMoney(s.sales_vat)} hint="SAR" icon={Receipt} tone="amber" testid="vat-sales-vat" delay={150} />
        </div>
        <NetBanner s={s} />
        <div className="grid xl:grid-cols-2 gap-4">
          <Table title="Purchases" testid="vat-purchases-table" head={[["Invoice date"], ["Invoice no."], ["Vendor"], ["Before VAT", 1], ["VAT", 1], ["Total", 1]]}
            rows={res.purchases.map((b) => [<span className="num text-xs">{fmtDate(b.invoice_date)}</span>, <span className="num text-xs">{b.invoice_number}</span>, b.vendor_name, <Money value={b.before_vat} />, <Money value={b.vat_paid} />, <Money value={b.total} strong />])} />
          <Table title="Sales" testid="vat-sales-table" head={[["Period"], ["Record"], ["Total sales", 1], ["VAT", 1], ["Net", 1]]}
            rows={res.sales.map((r) => [<span className="num text-xs">{fmtDate(r.period_from)} – {fmtDate(r.period_to)}</span>, <span className="num text-xs">{r.record_no}</span>, <Money value={r.total_sales} strong />, <Money value={r.vat_amount} />, <Money value={r.net_sales} />])} />
        </div>
      </>)}
      {clientId && <SharedReports clientId={clientId} tick={sharedTick} />}
      <ShareDialog open={share} onOpenChange={setShare} res={res} periodLabel={period.endsWith("_return") ? returnLabel(freq, range[0]) : ""} onShared={() => setSharedTick((x) => x + 1)} />
    </div>
  );
}
